// TanStack Query hooks for the applicant portal + client portal-user
// management (spec section 2.1 portal.*). The applicant routes (/api/portal/*)
// are row-scoped server-side to the caller's own job application; the
// portal-user routes (/api/orgs/:orgId/portal-users) are org-scoped and gated
// on portal.manage_org_users (or internal settings.manage_users). Mirrors
// packages/api/src/routes/portal.ts and portal-users.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePortalUserInput,
  PortalApplicationResponse,
  PortalDocument,
  PortalUserResponse,
  PortalUsersListResponse,
  UpdatePortalUserInput,
  UploadPortalDocumentInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'portal';

export const portalKeys = {
  all: [KEY] as const,
  application: () => [KEY, 'application'] as const,
  users: (orgId: number) => [KEY, 'users', orgId] as const,
};

// =============================================================================
// Applicant portal (/api/portal/*) — portal.read_status / upload_documents
// =============================================================================

export function useMyApplication(enabled = true) {
  return useQuery({
    queryKey: portalKeys.application(),
    enabled,
    queryFn: ({ signal }) =>
      api.get<PortalApplicationResponse>('/api/portal/application', signal),
  });
}

// POST /api/portal/application/documents — base64-encoded JSON upload (not the
// raw-bytes uploadFile path). Echoes back the catalogued document row.
interface UploadPortalDocumentResponse {
  document: PortalDocument;
}

export function useUploadApplicationDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadPortalDocumentInput) =>
      api.post<UploadPortalDocumentResponse>('/api/portal/application/documents', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalKeys.application() });
    },
  });
}

// =============================================================================
// Client portal users (/api/orgs/:orgId/portal-users) — portal.manage_org_users
// =============================================================================

export function usePortalUsers(orgId: number | null) {
  return useQuery({
    queryKey: portalKeys.users(orgId ?? 0),
    enabled: orgId !== null && orgId > 0,
    queryFn: ({ signal }) =>
      api.get<PortalUsersListResponse>(`/api/orgs/${orgId}/portal-users`, signal),
  });
}

export interface CreatePortalUserVars extends CreatePortalUserInput {
  orgId: number;
}

export function useCreatePortalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, ...input }: CreatePortalUserVars) =>
      api.post<PortalUserResponse>(`/api/orgs/${orgId}/portal-users`, input),
    onSuccess: (_data, { orgId }) => {
      void qc.invalidateQueries({ queryKey: portalKeys.users(orgId) });
    },
  });
}

export interface UpdatePortalUserVars extends UpdatePortalUserInput {
  orgId: number;
  userId: number;
}

export function useUpdatePortalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, userId, ...patch }: UpdatePortalUserVars) =>
      api.patch<PortalUserResponse>(`/api/orgs/${orgId}/portal-users/${userId}`, patch),
    onSuccess: (_data, { orgId }) => {
      void qc.invalidateQueries({ queryKey: portalKeys.users(orgId) });
    },
  });
}
